// @ts-strict-ignore
import { communicationService, UapLoadStatus } from '@ad-engine/communication';
import { BaseServiceSetup, context, InstantConfigService, utils } from '@ad-engine/core';
import { Injectable } from '@wikia/dependency-injection';
import { AD_ENGINE_UAP_LOAD_STATUS } from '../../communication/events/events-ad-engine-uap';
import { PlacementsHandler } from './utils/placements-handler';
import { trackOpenWebImpression } from '../../ad-tracking';

const logGroup = 'open-web';

interface OpenWebConfig {
	isActive: boolean;
	spotId: string;
}

@Injectable()
export class OpenWeb extends BaseServiceSetup {
	static MOBILE_REPLACE_REPEAT_SLOT_IDX = 2;
	private config: OpenWebConfig;
	private isListenerAdded = false;

	constructor(
		protected instantConfig: InstantConfigService,
		protected globalTimeout: utils.GlobalTimeout = null,
		private placementsHandler: PlacementsHandler = null,
	) {
		super(instantConfig, globalTimeout);
		this.readConfig(instantConfig);
	}

	call(): void {
		if (context.get('state.isLogged')) {
			utils.logger(logGroup, 'disabled - user is logged');
			return;
		}

		if (!this.isActive()) {
			utils.logger(logGroup, 'disabled - not activated');
			return;
		}

		const isRemoveOpenWebMobileAndAddICB = context.get('options.removeOpenWebAddICB');
		const isRemoveOpenDesktopAddMidBoxAd = context.get('experiments.midBoxAdExperiment');
		if (isRemoveOpenWebMobileAndAddICB || isRemoveOpenDesktopAddMidBoxAd) {
			const openWebRemovalReason = isRemoveOpenDesktopAddMidBoxAd
				? 'MidBoxAd will be added on Desktop.'
				: 'Additional ICB unit will be added on Mobile.';
			utils.logger(logGroup, `disabled - OpenWeb will be removed. Reason: ${openWebRemovalReason}`);
			return;
		}

		this.loadIfNoUap();
	}

	public isActive(): boolean {
		if (!this.config) {
			this.readConfig(this.instantConfig);
		}

		return this.config?.isActive || false;
	}

	private loadIfNoUap() {
		utils.logger(logGroup, 'Script is loaded in AE');

		communicationService.on(AD_ENGINE_UAP_LOAD_STATUS, (action: UapLoadStatus) => {
			if (action.isLoaded || action.adProduct === 'ruap') {
				utils.logger(logGroup, 'disabled - UAP is loaded');
				return;
			}

			this.placementsHandler.build();

			if (this.placementsHandler.isDone()) {
				this.registerListener();
				this.loadScript(this.config.spotId);
			} else {
				utils.logger(logGroup, 'disabled - builder failed');
			}
		});
	}

	private registerListener(): void {
		if (!this.isListenerAdded) {
			document.addEventListener('ow-analytics-stream', function (e: CustomEvent) {
				if (e.detail.engineStatus === 'dfp-impression-viewable') {
					trackOpenWebImpression();
				}
			});
			this.isListenerAdded = true;
			utils.logger(logGroup, 'Listener added', this.isListenerAdded);
		}
	}

	private readConfig(instantConfig: InstantConfigService): void {
		this.config = instantConfig.get('icOpenWeb', {
			isActive: false,
			spotId: 'n-a',
		});
	}

	private loadScript(spotId: string): void {
		const libraryUrl = `//launcher-sa.spot.im/spot/${spotId}`;

		utils.logger(logGroup, 'loading', libraryUrl);

		utils.timedPartnerScriptLoader.loadScriptWithStatus(
			libraryUrl,
			logGroup,
			true,
			null,
			{},
			{
				spotimModule: 'spotim-launcher',
			},
		);
	}
}
